import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Stack } from '@mui/material'
import { ReactNode } from 'react'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { DialogHeader } from './DialogHeader'

export type ButtonAction = {
  text: string
  variant?: 'text' | 'contained' | undefined
  color?: 'inherit' | 'error' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | undefined
  action: 'cancel' | 'confirm' | (() => void)
  disabled?: boolean
  dataTestId?: string
  loading?: boolean
}

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  bodyTexts: (ReactNode | string)[]
  actions: (ButtonAction & { alignLeft?: boolean })[]
  children?: ReactNode
  disableAutoCloseOnConfirm?: boolean
  showCloseButton?: boolean
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  bodyTexts,
  actions,
  children,
  disableAutoCloseOnConfirm,
  showCloseButton,
}: Props) {
  const handleButtonClick = async (buttonAction: ButtonAction) => {
    if (buttonAction.action === 'confirm') {
      onConfirm()
      if (!disableAutoCloseOnConfirm) {
        onClose()
      }
      return
    }
    if (buttonAction.action === 'cancel') {
      onClose()
      return
    }
    buttonAction.action()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogHeader title={title} onClose={showCloseButton ? onClose : undefined} />
      <DialogContent>
        <Stack direction="column" spacing={4}>
          {bodyTexts.filter(isDefined).map((bodyText, index) => (
            <DialogContentText key={index}>{bodyText}</DialogContentText>
          ))}
          {children && children}
        </Stack>
      </DialogContent>
      <DialogActions>
        {actions.map((action, index) => (
          <Button
            data-testid={action.dataTestId}
            key={index}
            variant={action.variant}
            color={action.color}
            onClick={() => handleButtonClick(action)}
            disabled={action.disabled}
            loading={action.loading}
            sx={{ ...(action.alignLeft ? { mr: 'auto' } : undefined) }}
          >
            {action.text}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  )
}
